import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, useTheme } from "@mui/material";
import { Typography } from "@mui/joy";
import { AddRounded } from "@mui-symbols-material/w300";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import _ from "lodash";

import { Direction, InstalledBaseNode } from "../../codegen/graphql";
import { InstalledBaseNodeList as InstalledBaseNodeListQuery } from "../../graphql/queries/installedBaseNodeList";
import { InstalledBaseRootNodeList as InstalledBaseRootNodeListQuery } from "../../graphql/queries/installedBaseRootNodeList";
import { InstalledBaseTreeItemNode as InstalledBaseTreeItemNodeQuery } from "../../graphql/queries/installedBaseTreeItemNode";
import { buildDynamicListFilter } from "../../utils/buildDynamicListFilter";
import { getColumnScrollDelta } from "../../utils/columnScroll";
import { decidePinnedSelection, PinnedSelectionDecision } from "../../utils/pinnedSelection";
import { groupColumnNodes } from "../../utils/groupColumnNodes";
import { canAddAnyChild, canOfferChildCreation } from "../../utils/nodeChildRules";
import { isNodeInTrash, isNodeInUserPool } from "../../utils/installedBasePath";
import { GuidanceAction } from "../../utils/emptyColumnGuidance";
import { TypeDefinitionAttribute } from "../../types";
import { nodeListPaginationLimit } from "../../config/paginationLimit";
import {
    focusFirstItem,
    focusItemAt,
    focusSearchInput,
    focusSelectedItemInColumn,
    getAdjacentColumn,
    getColumnEl,
    getColumnItems,
} from "../../utils/installedBaseKeyboardNav";

import InstalledBaseTreeItem from "../InstalledBaseTreeItem";
import InstalledBaseEmptyView from "../InstalledBaseEmptyView";
import ColumnAssetNameplate from "./ColumnAssetNameplate";
import SystemNameplate from "../SystemNameplate";
import NameplateCreateAction from "./nameplateCreateAction";
import { LoadingScreen } from "@dreebit-registry/vsm-microservice-components";
import NodeRowContextMenu, { NodeContextMenuState } from "./NodeRowContextMenu";
import { MoveInstalledBaseNodeSuccessInfo } from "../MoveInstalledBaseNodeModal";
import { getNodeDetailRoute } from "../../utils/getNodeShortcutTarget";

interface Props {
    parentId: string | null;
    parentNode: InstalledBaseNode;
    parentLoading?: boolean;
    searchQuery: string;
    selectedNodeId?: string | null;
    allSelectedNodeIds?: (string | null)[];
    onChangeNode: (id: string, options?: { viaKeyboard?: boolean }) => void;
    // true while this column should grab keyboard focus onto its first item
    // (set after a node was selected via keyboard in the parent column).
    focusFirstItemTrigger?: boolean;
    onFirstItemFocused?: () => void;
    filter?: { [key: string]: any };
    filterAttributes?: TypeDefinitionAttribute[];
    columnIndex: number;
    onNoChildren?: (index: number) => void;
    onAutoSkipRoot?: (nodeId: string) => void;
    onRootLoaded?: () => void;
    onRootEmpty?: (isEmpty: boolean) => void;
    /** `action` carries a pre-selected type from the empty-state guidance, when any. */
    onOpenCreateModal?: (action?: GuidanceAction) => void;
    onClearSearch?: () => void;
    onClearFilter?: () => void;
    refetchFunction?: () => void;
    deleteNodeCallback?: (deletedNodeId: string) => void;
    moveNodeCallback?: (info: MoveInstalledBaseNodeSuccessInfo) => void;
    onEditNodeDetail?: (nodeId: string) => void;
    onOpenNodeDetail?: (nodeId: string) => void;
    detailNodeId?: string | null;
    visible?: boolean;
}

const buildVars = (
    parentId: string | null,
    builtFilter: Record<string, any>,
    searchQuery: string,
    start: number,
    isRoot: boolean
) => {
    const base = {
        pagination: { start, limit: nodeListPaginationLimit },
        // Favoriten zuerst (eigene Gruppe, siehe groupColumnNodes). Titel explizit,
        // da der Default "Titel aufsteigend" nur ohne jedes Sort-Feld greift.
        sort: { starredAtTop: true, title: Direction.asc },
        ...(searchQuery ? { search: { query: searchQuery ? searchQuery : "", fields: ["title"] } } : {}),
    };

    if (isRoot) {
        return { ...base, filter: builtFilter };
    }
    return {
        ...base,
        filter: {
            parentNodeId: { equals: parentId },
            ...builtFilter,
        },
    };
};

// Fester Slot über der ersten Zeile jeder Spalte: darin steht das Label der
// obersten Gruppe ("Selected" bzw. "Favorites"), ohne Gruppen bleibt er leer —
// so beginnen die Listen aller Spalten auf gleicher Höhe.
const LIST_TOP_SLOT_HEIGHT = "20px";

const SectionLabel = ({ children, mt }: { children: React.ReactNode; mt?: string }) => (
    <Typography
        level="body-xs"
        sx={{
            height: LIST_TOP_SLOT_HEIGHT,
            lineHeight: "16px",
            p: "0 2px 4px",
            boxSizing: "border-box",
            mt,
        }}
    >
        {children}
    </Typography>
);

const nodesPath = (isRoot: boolean) =>
    isRoot
        ? "installedBaseRootNodeList.installedBaseNodes"
        : "installedBaseNodeList.installedBaseNodes";

// fetchMore-updateQuery: append the next page onto the merged list while
// deduping by id. Dedupe is essential — overlapping fetches or a
// `cache-and-network` start=0 refetch would otherwise produce duplicate rows.
// Must NOT mutate `fetchMoreResult` (`_.set` would): it is the object
// `fetchMore` resolves as `res.data`.
const mergePages = (prev: any, fetchMoreResult: any, isRoot: boolean) => {
    if (!fetchMoreResult) return prev;
    const rootKey = isRoot ? "installedBaseRootNodeList" : "installedBaseNodeList";
    const prevList = _.get(prev, nodesPath(isRoot)) ?? [];
    const nextList = _.get(fetchMoreResult, nodesPath(isRoot)) ?? [];
    const merged = _.uniqBy([...prevList, ...nextList], "id");
    return {
        ...fetchMoreResult,
        [rootKey]: { ...fetchMoreResult[rootKey], installedBaseNodes: merged },
    };
};

const InstalledBaseTreeList = ({
                                   parentId,
                                   parentNode,
                                   parentLoading,
                                   searchQuery,
                                   selectedNodeId,
                                   allSelectedNodeIds,
                                   onChangeNode,
                                   focusFirstItemTrigger,
                                   onFirstItemFocused,
                                   filter,
                                   filterAttributes,
                                   columnIndex,
                                   onNoChildren,
                                   onAutoSkipRoot,
                                   onRootLoaded,
                                   onRootEmpty,
                                   onOpenCreateModal,
                                   onClearSearch,
                                   onClearFilter,
                                   refetchFunction,
                                   deleteNodeCallback,
                                   moveNodeCallback,
                                   onEditNodeDetail,
                                   onOpenNodeDetail,
                                   detailNodeId,
                                   visible = true,
                               }: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();

    const selectedRef = useRef<HTMLDivElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const showMoreButtonRef = useRef<HTMLButtonElement | null>(null);
    const createButtonRef = useRef<HTMLButtonElement | null>(null);
    // Index des Items, das nach dem nächsten Nachladen den Fokus erhalten soll
    // (Enter auf "Show more" → erste neu geladene Zeile).
    const pendingFocusIndexRef = useRef<number | null>(null);
    const lastScrolledIdRef = useRef<string | null>(null);
    const visibleRef = useRef(visible);
    const selectedNodeIdRef = useRef<string | null | undefined>(selectedNodeId);
    const [contextMenuState, setContextMenuState] = useState<NodeContextMenuState | null>(null);
    const emptyReportedKeyRef = useRef<string | null>(null);
    const pinDecisionRef = useRef<PinnedSelectionDecision | null>(null);

    const isRoot = parentId === null;

    const builtFilter = useMemo(
        () => buildDynamicListFilter(filter ?? {}, (filterAttributes ?? []).filter(a => a.index !== "search")),
        [filter, filterAttributes]
    );

    // Query-Key für Reset-Bedingungen (ohne pagination/start)
    const queryKey = useMemo(
        () => JSON.stringify({ parentId, filter, searchQuery }),
        [parentId, filter, searchQuery]
    );

    const nodeListQuery = useQuery(InstalledBaseNodeListQuery, {
        variables: buildVars(parentId, builtFilter, searchQuery, 0, false),
        skip: isRoot,
        fetchPolicy: "cache-and-network",
        notifyOnNetworkStatusChange: true,
    });

    const rootListQuery = useQuery(InstalledBaseRootNodeListQuery, {
        variables: buildVars(parentId, builtFilter, searchQuery, 0, true),
        skip: !isRoot,
        fetchPolicy: "cache-and-network",
        notifyOnNetworkStatusChange: true,
    });

    const { data: rawData, loading, fetchMore } = isRoot ? rootListQuery : nodeListQuery;

    // Ein schmalerer Write einer fremden Query (z. B. der Node-Picker in einem
    // Modal) oder ein cache.evict kann den Cache-Read dieser Query unvollständig
    // machen; Apollo setzt `data` dann auf undefined und lädt neu. Ohne Fallback
    // würden die Rows über den LoadingScreen-Return unten unmounten — das
    // verliert die Scroll-Position und bewaffnet `scrollIntoView` beim Remount
    // erneut. Ein wirklich leeres Ergebnis kommt weiterhin als *definiertes*
    // data an, Empty-States bleiben also unberührt; der queryKey stellt sicher,
    // dass nach Parent-/Filter-/Suchwechsel nie die Rows des alten Parents stehen.
    const lastGoodRef = useRef<{ key: string; data: any } | null>(null);
    if (rawData) lastGoodRef.current = { key: queryKey, data: rawData };
    const data =
        rawData ?? (lastGoodRef.current?.key === queryKey ? lastGoodRef.current.data : undefined);

    const total = _.get(
        data,
        isRoot ? "installedBaseRootNodeList.total" : "installedBaseNodeList.total",
        0
    ) as number;

    const nodes: InstalledBaseNode[] = _.get(
        data,
        isRoot
            ? "installedBaseRootNodeList.installedBaseNodes"
            : "installedBaseNodeList.installedBaseNodes",
        []
    );

    // --- Ausgewählter Node außerhalb der ersten Seite: oben angeheftet zeigen,
    // statt die Liste bis zu ihm durchzupaginieren (bei 1000+ Root-Nodes waren
    // das Dutzende sequenzielle Requests). Entschieden wird einmal pro Query +
    // Auswahl anhand der ersten Seite (siehe decidePinnedSelection). Ein Klick
    // in der Spalte wählt immer einen geladenen Node, pinnt also nie.
    pinDecisionRef.current = decidePinnedSelection(
        pinDecisionRef.current,
        queryKey,
        selectedNodeId,
        // Erst mit dem Netzwerk-Ergebnis: cache-and-network liefert nach einem
        // Remount zuerst die gecachte Liste inkl. "Show more"-Seiten, die der
        // Refetch wieder auf Seite 1 kürzt — darauf entschieden, wäre der Node
        // weder gepinnt noch in der Liste.
        rawData && !loading ? nodes.map((n) => n.id) : undefined
    );
    const pinDecision = pinDecisionRef.current;

    // Startet parallel zur ersten Seite (Entscheidung noch offen) und wird
    // übersprungen, sobald feststeht, dass nichts gepinnt wird — normale Klicks
    // kosten so keinen zusätzlichen Request.
    const pinQuery = useQuery(InstalledBaseTreeItemNodeQuery, {
        variables: { id: selectedNodeId },
        skip: !selectedNodeId || pinDecision?.pinned === false,
        fetchPolicy: "cache-first",
    });
    const pinnedNode: InstalledBaseNode | undefined =
        pinDecision?.pinned && pinQuery.data?.installedBaseNode?.id === selectedNodeId
            ? pinQuery.data.installedBaseNode
            : undefined;
    const pinLoading = !!pinDecision?.pinned && pinQuery.loading && !pinnedNode;
    // Der Pin belegt im DOM den ersten Item-Slot der Spalte.
    const itemIndexOffset = pinnedNode ? 1 : 0;

    // Favoriten als eigene Gruppe; der gepinnte Node steht in keiner der beiden.
    // Pagination rechnet weiter mit `nodes`, DOM-Indizes mit `renderedCount`.
    const { favorites, others } = groupColumnNodes(nodes, pinnedNode?.id);
    const renderedCount = favorites.length + others.length;

    useEffect(() => {
        lastScrolledIdRef.current = null;
    }, [queryKey]);

    // --- Pagination (append) via fetchMore + updateQuery
    const hasMore = nodes.length < total;

    const loadMore = useCallback(async () => {
        if (!hasMore) return;
        const nextStart = nodes.length;
        await fetchMore({
            variables: buildVars(parentId, builtFilter, searchQuery, nextStart, isRoot),
            updateQuery: (prev, { fetchMoreResult }) => mergePages(prev, fetchMoreResult, isRoot),
        });
    }, [fetchMore, hasMore, nodes.length, parentId, builtFilter, searchQuery, isRoot]);

    // --- Reset flags when selection changes
    useEffect(() => {
        lastScrolledIdRef.current = null;
        selectedNodeIdRef.current = selectedNodeId;
    }, [selectedNodeId]);

    // --- Spalten schließen, wenn leer (nur unfiltert)
    const isUnfiltered =
        !searchQuery && (!filter || Object.keys(filter).length === 0);

    useEffect(() => {
        if (!loading && total === 0 && isUnfiltered) {
            const key = `${parentId ?? "ROOT"}|${columnIndex}`;
            if (emptyReportedKeyRef.current !== key) {
                emptyReportedKeyRef.current = key;
                onNoChildren?.(columnIndex);
            }
        }
    }, [loading, total, isUnfiltered, parentId, columnIndex, onNoChildren]);

    // --- Erste Spalte: Auto-skip
    useEffect(() => {
        // Only apply to root column
        if (parentId !== null) return;

        // Don't process while still loading
        if (loading) return;

        // At this point: root column, loading complete, we have final data

        // Case 1: Exactly one node + unfiltered → auto-skip root.
        // Only onAutoSkipRoot here — it selects the node AND preserves an
        // already-restored deeper path. onChangeNode must not be called for
        // this: it is click semantics (deselect toggle) and cut a deep-linked
        // path down to the root on every mount.
        if (isUnfiltered && total === 1 && nodes.length === 1) {
            const onlyNode = nodes[0];
            if (onlyNode?.id) {
                onRootEmpty?.(false);
                onAutoSkipRoot?.(onlyNode.id);
            }
            return; // Auto-skip handles rootCheckDone via handleAutoSkipRoot
        }

        // Case 2: No accessible companies (unfiltered) → report empty so the
        // tree can render a full-content no-access message instead of columns.
        if (isUnfiltered && total === 0) {
            onRootEmpty?.(true);
            onRootLoaded?.();
            return;
        }

        // Case 3: All other cases (>1 nodes, filtered, search, etc.)
        // Always call onRootLoaded to mark root column as ready
        onRootEmpty?.(false);
        onRootLoaded?.();

    }, [loading, parentId, total, nodes, isUnfiltered, onChangeNode, onAutoSkipRoot, onRootLoaded, onRootEmpty]);

    // --- Scroll zu ausgewähltem Node.
    // Über eine stabile Ref-Callback statt useEffect: feuert deterministisch
    // sobald das Item ins DOM kommt (egal ob durch Pagination, parentLoading-
    // Flip oder Cache-Reset → Re-Mount). Über visibleRef/selectedNodeIdRef,
    // damit der Callback stabil bleibt und nicht bei jedem Re-Render von
    // React neu attached/detached wird.
    useEffect(() => {
        visibleRef.current = visible;
    }, [visible]);

    const attemptScroll = useCallback(() => {
        const el = selectedRef.current;
        if (!el) return;
        if (!visibleRef.current) return;
        const id = selectedNodeIdRef.current;
        if (!id) return;
        if (lastScrolledIdRef.current === id) return;
        // Mark first to dedupe concurrent triggers (ref-callback + effect).
        lastScrolledIdRef.current = id;
        // rAF: ensure layout is settled after display:none → block flips.
        requestAnimationFrame(() => {
            const node = selectedRef.current;
            const scroller = containerRef.current;
            if (!node || !scroller) return;
            // Nur so weit scrollen, dass die Zeile sichtbar ist — vorher wurde sie
            // immer zentriert, was bei jeder Auswahl einen sichtbaren Sprung
            // erzeugte. Direkt über scrollTop statt scrollIntoView: letzteres
            // scrollt ALLE scrollbaren Vorfahren mit, also auch den horizontalen
            // Tree-Container, und stört dessen Smooth-Scroll-Animation.
            const delta = getColumnScrollDelta(
                scroller.getBoundingClientRect(),
                node.getBoundingClientRect()
            );
            if (delta !== 0) scroller.scrollTop += delta;
        });
    }, []);

    // Ref-Callback für das selektierte Item: feuert bei mount/unmount.
    const handleSelectedItemRef = useCallback((el: HTMLDivElement | null) => {
        selectedRef.current = el;
        if (el) {
            attemptScroll();
        } else {
            // Item ist unmounted (z. B. nach Cache-Reset). Re-Scroll erlauben,
            // wenn das Item später wieder mountet.
            lastScrolledIdRef.current = null;
        }
    }, [attemptScroll]);

    // Wenn die Spalte sichtbar wird (Root-Check fertig), Scroll erneut versuchen.
    useEffect(() => {
        if (visible) attemptScroll();
    }, [visible, attemptScroll]);

    // --- Keyboard-Navigation
    // Nach Auswahl per Tastatur in der Elternspalte: erstes Item fokussieren,
    // sobald die Liste fertig geladen ist. Flag auch bei leerer Liste
    // (Nameplate/Empty-View) zurücksetzen, damit es nicht hängen bleibt.
    useEffect(() => {
        if (!focusFirstItemTrigger) return;
        if (loading || (parentLoading && !isRoot)) return;
        // preventScroll: das horizontale Smooth-Scrolling des Trees nicht
        // unterbrechen; das erste Item steht ohnehin oben.
        focusFirstItem(containerRef.current, { preventScroll: true });
        onFirstItemFocused?.();
    }, [focusFirstItemTrigger, loading, parentLoading, isRoot, nodes.length, onFirstItemFocused]);

    // Enter auf "Show more": Fokus auf die erste neu geladene Zeile, sobald
    // sie im DOM ist.
    useEffect(() => {
        const pending = pendingFocusIndexRef.current;
        if (pending === null) return;
        if (renderedCount + itemIndexOffset > pending) {
            pendingFocusIndexRef.current = null;
            focusItemAt(containerRef.current, pending);
        }
    }, [renderedCount, itemIndexOffset]);

    const handleItemKeyDown = (
        e: React.KeyboardEvent<HTMLDivElement>,
        node: InstalledBaseNode
    ) => {
        // Enter aus inneren fokussierbaren Elementen (z. B. Detail-IconButton)
        // nicht als Auswahl interpretieren.
        if (e.key === 'Enter' && e.target !== e.currentTarget) return;

        const columnEl = getColumnEl(e.currentTarget);
        // Index im DOM statt in `nodes`: ein angehefteter Node steht davor.
        const index = getColumnItems(columnEl).indexOf(e.currentTarget);
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                // Past the last row, focus whichever bottom button is rendered
                // ("Show more" and "Create Node" are mutually exclusive).
                if (!focusItemAt(columnEl, index + 1)) {
                    (hasMore ? showMoreButtonRef : createButtonRef).current?.focus();
                }
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (index === 0) {
                    focusSearchInput(columnEl);
                } else {
                    focusItemAt(columnEl, index - 1);
                }
                break;
            case 'ArrowRight':
                e.preventDefault();
                if (node.id === selectedNodeId) {
                    // Bereits ausgewählt → direkt in die nächste Spalte springen.
                    focusFirstItem(getAdjacentColumn(columnEl, 'next'), { preventScroll: true });
                } else {
                    onChangeNode(node.id, { viaKeyboard: true });
                }
                break;
            case 'Enter':
                e.preventDefault();
                onChangeNode(node.id, { viaKeyboard: true });
                break;
            case 'ArrowLeft':
                e.preventDefault();
                focusSelectedItemInColumn(getAdjacentColumn(columnEl, 'previous'));
                break;
            case 'Escape':
                focusSearchInput(columnEl);
                break;
        }
    };

    // Enter/Space activate the button natively — unlike "Show more" there is no
    // double-fire to suppress, so only the list-return needs handling.
    const handleCreateButtonKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            focusItemAt(getColumnEl(e.currentTarget), renderedCount - 1 + itemIndexOffset);
        }
    };

    const handleShowMoreKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            focusItemAt(getColumnEl(e.currentTarget), renderedCount - 1 + itemIndexOffset);
        } else if (e.key === 'Enter') {
            // Native Click-Aktivierung unterdrücken, damit loadMore nur einmal
            // läuft und der Fokus deterministisch gesetzt werden kann.
            e.preventDefault();
            pendingFocusIndexRef.current = renderedCount + itemIndexOffset;
            loadMore();
        }
    };

    const renderRow = (
        node: InstalledBaseNode,
        options: {
            ref?: ((el: HTMLDivElement | null) => void) | null;
            pinned?: boolean;
            style?: React.CSSProperties;
        }
    ) => (
        <InstalledBaseTreeItem
            key={node.id}
            node={node}
            ref={options.ref}
            pinned={options.pinned}
            selected={allSelectedNodeIds?.includes(node.id)}
            contextActive={contextMenuState?.node?.id === node.id}
            onClick={() => onChangeNode(node.id)}
            onKeyDown={(e) => handleItemKeyDown(e, node)}
            onOpenDetailView={onOpenNodeDetail ? () => onOpenNodeDetail(node.id) : undefined}
            detailRoute={getNodeDetailRoute(node)}
            detailOpen={detailNodeId === node.id}
            onContextMenu={(e, n) => {
                // IBM-586: the TRASH node itself offers no actions —
                // don't open (and highlight) an empty context menu.
                if (n?.nodeType?.key === 'TRASH') return;
                setContextMenuState({
                    node: n,
                    position: { top: e.clientY, left: e.clientX },
                });
            }}
            style={options.style}
        />
    );

    const renderGroupRows = (groupNodes: InstalledBaseNode[]) =>
        groupNodes.map((node, index) =>
            renderRow(node, {
                // Ein gepinnter Node steht in keiner Gruppe, ist also nie Ziel des
                // Scrolls — sonst spränge die Spalte, sobald "Show more" ihn nachlädt.
                ref: node.id === selectedNodeId ? handleSelectedItemRef : null,
                style:
                    index === 0
                        ? { borderTop: `1px solid ${theme.palette.divider}` }
                        : {},
            })
        );

    // --- Loading-State: nur initial, sonst unten "Show more" deaktivieren
    // Auch laden, wenn parent noch lädt (außer bei Root, da parentId === null)
    // Erst rendern, wenn auch ein anzuheftender Node da ist — sonst schöbe er die
    // bereits sichtbare Liste nachträglich nach unten.
    if ((loading && nodes.length === 0) || (parentLoading && !isRoot) || pinLoading) {
        return <LoadingScreen size="sm" />;
    }

    // --- Check if parent is an Asset or System type that should show nameplate instead of empty view
    const shouldShowAssetNameplate = parentNode?.nodeType?.key === "ASSET" && !!parentNode?.assetLocation?.id;
    const shouldShowSystemNameplate = parentNode?.nodeType?.key === "SYSTEM" && !!parentNode?.system?.id;
    const shouldShowNameplate = shouldShowAssetNameplate || shouldShowSystemNameplate;

    // IBM-586: nothing may be created inside the trash, and trashed elements only
    // offer Restore + Delete. Covers the TRASH column itself and any depth below it.
    const columnInTrash = isNodeInTrash(parentNode);

    // --- Bottom create button: only for a populated list with nothing left to page in,
    // so it never competes with "Show more" and never doubles up with the empty state.
    const showBottomCreateButton =
        nodes.length > 0 &&
        nodes.length >= total &&
        !columnInTrash &&
        canOfferChildCreation(isRoot, parentNode?.nodeRules, !!parentNode?.access?.write) &&
        canAddAnyChild(parentNode?.nodeRules);

    return (
        <Box ref={containerRef} sx={{ overflowY: "auto", flexGrow: 1, p: "0 20px" }}>
            {/* Vor dem Empty-State: die Auswahl bleibt oben, auch wenn eine
                nachträgliche Suche nichts findet. */}
            {pinnedNode && (
                <Box
                    sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                        bgcolor: "#fff",
                        // Abstand zur Liste als Padding (nicht Margin), damit beim
                        // Scrollen keine Zeilen durch die Lücke scheinen.
                        pb: "12px",
                    }}
                >
                    <SectionLabel>{t("Selected")}</SectionLabel>
                    {renderRow(pinnedNode, {
                        pinned: true,
                        style: { borderTop: `1px solid ${theme.palette.divider}` },
                    })}
                </Box>
            )}

            {total === 0 && !shouldShowNameplate && (
                <InstalledBaseEmptyView
                    parentNode={parentNode}
                    searchQuery={searchQuery}
                    filter={filter}
                    canCreate={parentId !== null && !columnInTrash && canAddAnyChild(parentNode?.nodeRules)}
                    onCreate={onOpenCreateModal}
                    onClearSearch={onClearSearch}
                    onClearFilter={onClearFilter}
                />
            )}
            {total === 0 && shouldShowAssetNameplate && (
                <Box sx={{p: "0 16px", display: "flex", flexDirection: "column"}}>
                    <ColumnAssetNameplate
                        assetLocationId={parentNode.assetLocation!.id}
                        installedBaseNodeId={parentNode.id}
                        assetId={parentNode.assetLocation?.asset?.id}
                        onOpenAsset={() => navigate(`/assets/${parentNode.assetLocation!.id}`)}
                        onRemoved={refetchFunction}
                        hideRemoveAction={columnInTrash}
                    />
                    {/* Sibling rather than an AssetNameplate prop: that component is shared
                        with the asset detail views, which must not gain the button. */}
                    {!columnInTrash && (
                        <NameplateCreateAction
                            parentNode={parentNode}
                            onCreate={(action) => onOpenCreateModal?.(action)}
                        />
                    )}
                </Box>
            )}
            {total === 0 && shouldShowSystemNameplate && (
                <Box sx={{p: "0 16px", display: "flex", flexDirection: "column"}}>
                    <SystemNameplate
                        systemId={parentNode.system!.id}
                        onOpenSystem={() => navigate(`/systems/${parentNode.system!.id}/details`)}
                        // Image upload lives in the detail views only.
                        hideImageActions
                    />
                    {/* Sibling rather than a SystemNameplate prop: that component is shared
                        with the system detail navigation, which must not gain the button. */}
                    {!columnInTrash && (
                        <NameplateCreateAction
                            parentNode={parentNode}
                            onCreate={(action) => onOpenCreateModal?.(action)}
                        />
                    )}
                </Box>
            )}

            {favorites.length > 0 && (
                <>
                    <SectionLabel>{t("Favorites")}</SectionLabel>
                    {renderGroupRows(favorites)}
                </>
            )}

            {favorites.length > 0 && others.length > 0 && (
                <SectionLabel mt="12px">{t("All")}</SectionLabel>
            )}
            {favorites.length === 0 && !pinnedNode && others.length > 0 && (
                <Box sx={{ height: LIST_TOP_SLOT_HEIGHT }} />
            )}
            {renderGroupRows(others)}

            <NodeRowContextMenu
                state={contextMenuState}
                isRoot={isRoot}
                // Rows are children of the column node: they lie in the trash when
                // the column node is the trash or sits inside it.
                inTrash={columnInTrash}
                inUserPool={isNodeInUserPool(parentNode)}
                onClose={() => setContextMenuState(null)}
                refetchFunction={refetchFunction}
                deleteNodeCallback={deleteNodeCallback}
                moveNodeCallback={moveNodeCallback}
                onEditNodeDetail={onEditNodeDetail}
            />

            {/** Show more nur anzeigen, wenn es schon Items gibt UND noch mehr vorhanden sind */}
            {nodes.length > 0 && nodes.length < total && (
                <Box textAlign="center" mt={2}>
                    <Button
                        ref={showMoreButtonRef}
                        onClick={loadMore}
                        onKeyDown={handleShowMoreKeyDown}
                        variant="text"
                        disabled={loading}
                    >
                        {loading ? <LoadingScreen size="sm" /> : t("Show more")}
                    </Button>
                </Box>
            )}

            {/** Takes the "Show more" slot once everything is loaded: the header "+" is
                 hover-gated and easy to miss in a sparsely-filled column. Hidden (not
                 disabled) at the child limit — the header "+" explains that case. */}
            {showBottomCreateButton && (
                <Box textAlign="center" mt={2}>
                    <Button
                        ref={createButtonRef}
                        onClick={() => onOpenCreateModal?.()}
                        onKeyDown={handleCreateButtonKeyDown}
                        variant="text"
                        startIcon={<AddRounded fontSize="small" />}
                        sx={{ color: "#666666" }}
                    >
                        {t("Create Node")}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default InstalledBaseTreeList;
